// WebSocket message types
// src/lib/types/websocket/messages.types.ts
import type { User } from '../core/user.types';
import type { WorkOrder } from '../domain/workOrders.types';

export interface BaseMessage {
	id?: string | number;
	timestamp: string | number;
	type: MessageType | string;
	sequence?: string;
	schema_version?: string;
}

export enum MessageType {
	// Connection related
	CONNECT = 'CONNECT',
	DISCONNECT = 'DISCONNECT',
	CONNECTION_ACK = 'CONNECTION_ACK',
	CONNECTION_STATUS = 'CONNECTION_STATUS',

	// Chat message types
	SEND_CHAT_MESSAGE = 'SEND_CHAT_MESSAGE',
	CHAT_MESSAGE_RESPONSE = 'CHAT_MESSAGE_RESPONSE',
	CHAT_MESSAGE = 'CHAT_MESSAGE',
	JOIN_CHAT_ROOM = 'JOIN_CHAT_ROOM',
	LEAVE_CHAT_ROOM = 'LEAVE_CHAT_ROOM',
	CHAT_ROOM_JOINED = 'CHAT_ROOM_JOINED',
	TYPING_INDICATOR = 'TYPING_INDICATOR',
	GET_CHAT_HISTORY = 'GET_CHAT_HISTORY',
	CHAT_HISTORY_RESPONSE = 'CHAT_HISTORY_RESPONSE',
	MARK_MESSAGE_READ = 'MARK_MESSAGE_READ',
	CHAT_INITIAL = 'CHAT_INITIAL',

	// Notification message types
	MARK_NOTIFICATION_READ = 'MARK_NOTIFICATION_READ',
	MARK_ALL_NOTIFICATIONS_READ = 'MARK_ALL_NOTIFICATIONS_READ',
	NOTIFICATION_READ_RESPONSE = 'NOTIFICATION_READ_RESPONSE',
	NEW_NOTIFICATION = 'NEW_NOTIFICATION',
	NOTIFICATION = 'NOTIFICATION',
	NOTIFICATION_COUNT_UPDATE = 'NOTIFICATION_COUNT_UPDATE',
	GET_NOTIFICATION_HISTORY = 'GET_NOTIFICATION_HISTORY',
	NOTIFICATION_HISTORY_RESPONSE = 'NOTIFICATION_HISTORY_RESPONSE',
	UPDATE_NOTIFICATION_PREFERENCES = 'UPDATE_NOTIFICATION_PREFERENCES',
	GET_NOTIFICATION_COUNT = 'GET_NOTIFICATION_COUNT',
	DISMISS_NOTIFICATION = 'DISMISS_NOTIFICATION',
	NOTIFICATIONS_INITIAL = 'NOTIFICATIONS_INITIAL',

	// Emergency broadcast
	EMERGENCY_BROADCAST = 'EMERGENCY_BROADCAST',

	// Permission / RBAC live update (server -> client; triggers a live permission refresh)
	PERMISSION_UPDATE = 'PERMISSION_UPDATE',

	// Subscription related
	SUBSCRIBE = 'SUBSCRIBE',
	UNSUBSCRIBE = 'UNSUBSCRIBE',
	SUBSCRIPTION_ACK = 'SUBSCRIPTION_ACK',
	SUBSCRIPTION_STATUS = 'SUBSCRIPTION_STATUS',

	// Data related
	DATA_UPDATE = 'DATA_UPDATE',
	DATA_INSERT = 'DATA_INSERT',
	DATA_DELETE = 'DATA_DELETE',
	BULK_UPDATE = 'BULK_UPDATE',

	// Status related
	ERROR = 'ERROR',
	AUTH_ERROR = 'AUTH_ERROR',
	CHAT_ERROR = 'CHAT_ERROR',
	NOTIFICATION_ERROR = 'NOTIFICATION_ERROR',
	HEARTBEAT = 'HEARTBEAT',
	PING = 'PING',
	PONG = 'PONG',
	STATUS = 'STATUS',

	// Alert types
	FILTERED_ALERTS_UPDATE = 'FILTERED_ALERTS_UPDATE',

	// Refresh signal message types (no payload, just triggers a refresh)
	ALERTS_REFRESH_REQUIRED = 'ALERTS_REFRESH_REQUIRED',
	BATCH_ALERTS_COMPLETE = 'BATCH_ALERTS_COMPLETE',
	WORK_ORDERS_REFRESH_REQUIRED = 'WORK_ORDERS_REFRESH_REQUIRED',

	// =============================================================================
	// UNIFIED DATA_DELTA / DATA_SNAPSHOT PATTERN
	// =============================================================================
	// These are the ONLY message types for data updates. The old entity-specific
	// message types have been removed as part of the unification migration:
	// - ALERTS_UPDATE, ALERT_INCREMENTAL_UPDATE, ALERT_UPDATE → DATA_SNAPSHOT / DATA_DELTA
	// - WORK_ORDERS_UPDATE, WORK_ORDER_INCREMENTAL_UPDATE → DATA_SNAPSHOT / DATA_DELTA
	// - ALERTS_REMOVED_FOR_WORK_ORDER → DATA_DELTA with action='remove'
	// =============================================================================
	DATA_DELTA = 'DATA_DELTA',
	DATA_DELTA_BATCH = 'DATA_DELTA_BATCH',
	DATA_SNAPSHOT = 'DATA_SNAPSHOT',
	TECH_WORK_ORDERS_UPDATE = 'TECH_WORK_ORDERS_UPDATE',
	INITIAL_STATE = 'INITIAL_STATE',
	DATA_REQUEST_RESPONSE = 'DATA_REQUEST_RESPONSE',
	STATUS_RESPONSE = 'STATUS_RESPONSE',

	// User Presence message types
	GET_ONLINE_USERS = 'GET_ONLINE_USERS',
	ONLINE_USERS_RESPONSE = 'ONLINE_USERS_RESPONSE',
	ONLINE_USERS = 'ONLINE_USERS',
	GET_USER_STATUS = 'GET_USER_STATUS',
	USER_STATUS_RESPONSE = 'USER_STATUS_RESPONSE',
	UPDATE_USER_PRESENCE = 'UPDATE_USER_PRESENCE',
	PRESENCE_UPDATE_SUCCESS = 'PRESENCE_UPDATE_SUCCESS',
	GET_PRESENCE_STATS = 'GET_PRESENCE_STATS',
	PRESENCE_STATS_RESPONSE = 'PRESENCE_STATS_RESPONSE',
	SYSTEM_PRESENCE_UPDATE = 'SYSTEM_PRESENCE_UPDATE',
	VIEWER_STATE_UPDATE = 'VIEWER_STATE_UPDATE',

	UPDATE = 'UPDATE',
	UPDATE_RESPONSE = 'UPDATE_RESPONSE',

	STATE_UPDATE = 'STATE_UPDATE',
	STATE_UPDATE_RESPONSE = 'STATE_UPDATE_RESPONSE',
	STATE_UPDATE_ACK = 'STATE_UPDATE_ACK',
	GET_STATE = 'GET_STATE',
	STATE_DATA = 'STATE_DATA',

	// Auto-subscription flow messages
	INITIAL_DATA_COMPLETE = 'INITIAL_DATA_COMPLETE',
	REQUEST_DATA = 'REQUEST_DATA',

	// Scope handshake (per spec 2026-05-11 — close WS data-path scope leak)
	SCOPE_CHANGE = 'SCOPE_CHANGE',
	SCOPE_CHANGED = 'SCOPE_CHANGED',
	SCOPE_CHANGE_ERROR = 'SCOPE_CHANGE_ERROR',

	// Additional message types
	NOTIFICATIONS_UPDATE = 'NOTIFICATIONS_UPDATE',
	DATA_SENT = 'DATA_SENT',
	CONNECTION_ESTABLISHED = 'CONNECTION_ESTABLISHED',
	PRESENCE_UPDATED = 'PRESENCE_UPDATED',
	// Task progress updates
	TASK_PROGRESS = 'TASK_PROGRESS',

	// AI Triage notifications
	AI_TRIAGE_COMPLETE = 'AI_TRIAGE_COMPLETE',

	// Unified Alert Analysis — pushed per-alert as auto-triage processes
	ALERT_ANALYSIS_UPDATE = 'ALERT_ANALYSIS_UPDATE'
}

export interface StateUpdatePayload {
	update_type: 'state_update';
	namespace: string;
	key?: string;
	data: any;
	timestamp?: string;
}

export interface StateUpdateMessage extends BaseMessage {
	type: MessageType.STATE_UPDATE;
	namespace: string;
	key?: string;
	data: Record<string, unknown>;
	broadcast?: boolean;
	ttl?: number;
}

export interface StateUpdateResponseMessage extends BaseMessage {
	type: MessageType.STATE_UPDATE_RESPONSE;
	status: 'success' | 'error';
	namespace: string;
	key?: string;
	// Add this if you want consistent handling:
	data?: Record<string, unknown>;
}

export interface GetStateMessage extends BaseMessage {
	type: MessageType.GET_STATE;
	namespace: string;
	key?: string;
}

export interface StateDataMessage extends BaseMessage {
	type: MessageType.STATE_DATA;
	namespace: string;
	key?: string;
	data: Record<string, unknown>;
}
export interface UpdateData {
	update_id: string;
	update_type: 'user_action' | 'system_event' | 'notification' | 'presence' | 'data_sync';
	entity_type: string;
	entity_id: string;
	changes: Record<string, { old: unknown; new: unknown }>;
	source_user_id?: string; // Added by server
}

export interface UpdateMessage extends BaseMessage {
	type: MessageType.UPDATE;
	data: UpdateData;
}

export interface UpdateChange {
	old: any;
	new: any;
}

export interface TableItem {
	id: string | number;
	[key: string]: unknown;
}

// Connection messages
export interface ConnectMessage extends BaseMessage {
	type: MessageType.CONNECT;
	clientId?: string;
	auth?: {
		token: string;
	};
}

export interface ConnectionAckMessage extends BaseMessage {
	type: MessageType.CONNECTION_ACK;
	clientId: string;
	status: 'success' | 'error';
	message?: string;
}

// Subscription messages
export interface SubscribeMessage extends BaseMessage {
	type: MessageType.SUBSCRIBE;
	data: {
		topics: string[];
		filters?: Record<string, unknown>;
	};
}

export interface SubscriptionAckMessage extends BaseMessage {
	type: MessageType.SUBSCRIPTION_ACK;
	tables: string[];
	status: 'success' | 'error';
	message?: string;
}

// Data messages
export interface DataUpdateMessage extends BaseMessage {
	type: MessageType.DATA_UPDATE;
	table: string;
	data: TableItem;
	previousData?: Partial<TableItem>;
}

export interface DataInsertMessage extends BaseMessage {
	type: MessageType.DATA_INSERT;
	table: string;
	data: TableItem;
}

export interface DataDeleteMessage extends BaseMessage {
	type: MessageType.DATA_DELETE;
	table: string;
	id: string | number;
}

export interface BulkUpdateMessage extends BaseMessage {
	type: MessageType.BULK_UPDATE;
	table: string;
	data: TableItem[];
}

// Status messages
export interface ErrorMessage extends BaseMessage {
	type: MessageType.ERROR;
	code: string;
	message: string;
	details?: unknown;
}

export interface HeartbeatMessage extends BaseMessage {
	type: MessageType.HEARTBEAT;
	clientId: string;
}

export interface WebSocketPingMessage extends BaseMessage {
	type: MessageType.PING;
}

export interface WebSocketPongMessage extends BaseMessage {
	type: MessageType.PONG;
}

export interface StatusMessage extends BaseMessage {
	type: MessageType.STATUS;
	status: 'online' | 'offline' | 'maintenance';
	message?: string;
}

// =============================================================================
// LEGACY MESSAGE INTERFACES REMOVED
// =============================================================================
// The following interfaces have been removed as part of the unified migration:
// - WorkOrdersUpdateMessage (WORK_ORDERS_UPDATE) → Use DataSnapshotMessage
// - AlertsUpdateMessage (ALERTS_UPDATE) → Use DataSnapshotMessage
// - AlertIncrementalUpdateMessage (ALERT_INCREMENTAL_UPDATE) → Use DataDeltaMessage
// - WorkOrderIncrementalUpdateMessage (WORK_ORDER_INCREMENTAL_UPDATE) → Use DataDeltaMessage
// =============================================================================

// Filtered Alerts Message
export interface FilteredAlertsUpdateMessage extends BaseMessage {
	type: MessageType.FILTERED_ALERTS_UPDATE;
	data: Array<{
		asset_name: string;
		manager: string;
		distinct_alert_types_count: number;
		alert_details: string;
		all_alert_names: string;
		all_statuses?: string;
		all_created_at?: string;
		tracker_loss?: number;
		fetched_at?: string;
	}>;
	count?: number;
	topic?: string;
}

// Alert group entry type - still used by DataSnapshotMessage for alerts entity
export interface AlertGroupMessageEntry {
	asset_id: string;
	asset_name: string;
	manager?: string;
	alerts: Array<Record<string, unknown>>;
	daily_data?: Array<Record<string, unknown>>;
	metadata?: Record<string, unknown>;
}

// =============================================================================
// UNIFIED DATA_DELTA / DATA_SNAPSHOT MESSAGE TYPES
// =============================================================================
// These unified message types replace entity-specific message types for a
// cleaner, more maintainable WebSocket architecture.
//
// Migration path:
// - DATA_DELTA replaces: ALERT_INCREMENTAL_UPDATE, WORK_ORDER_INCREMENTAL_UPDATE,
//   ALERT_UPDATE, ALERTS_REMOVED_FOR_WORK_ORDER
// - DATA_SNAPSHOT replaces: ALERTS_UPDATE, WORK_ORDERS_UPDATE
// =============================================================================

/**
 * Unified message for ALL incremental updates (add/update/remove).
 * Routes to the appropriate orchestrator handler based on entity type.
 */
export interface DataDeltaMessage extends BaseMessage {
	type: MessageType.DATA_DELTA;
	/** Entity type being updated */
	entity: 'alerts' | 'work_orders' | 'approved_alerts' | 'notifications';
	/** Action being performed */
	action: 'add' | 'update' | 'remove';
	/** Primary ID for single-item operations */
	id?: string;
	/** Multiple IDs for bulk operations (e.g., removing multiple alerts) */
	ids?: string[];
	/** The entity data (normalized from alert, work_order, data fields) */
	data: Record<string, unknown>;
	/** Optional reason for the change (e.g., 'work_order_created', 'approved', 'rejected') */
	reason?: string;
	/** Optional change timestamp */
	change_ts?: string;
	/** Additional metadata */
	metadata?: Record<string, unknown>;
}

/**
 * Batched delta message wrapping multiple DataDeltaMessage items into a single
 * WebSocket frame. The frontend unpacks each item and processes them together
 * in a single reactive cycle, reducing UI flicker during bulk operations.
 */
export interface DataDeltaBatchMessage extends BaseMessage {
	type: MessageType.DATA_DELTA_BATCH;
	/** Array of delta items - each has the same shape as DataDeltaMessage fields */
	items: Array<{
		entity: 'alerts' | 'work_orders' | 'approved_alerts' | 'notifications';
		action: 'add' | 'update' | 'remove';
		id?: string;
		ids?: string[];
		data: Record<string, unknown>;
		reason?: string;
		change_ts?: string;
		metadata?: Record<string, unknown>;
	}>;
	/** Total count for logging / pre-allocation */
	count: number;
}

/**
 * Unified message for ALL full data loads (initial load, refresh).
 * Routes to the appropriate orchestrator handler based on entity type.
 *
 * Phase 6a: Aligned with the Zod schema in websocket/schemas.ts. The
 * previous type was stale — it didn't model the grouped-alerts payload
 * shape (regular_alerts + approved_alerts object), the hardware_alerts
 * shape (items + groups), or the top-level snapshot_ts / failed_component
 * fields the backend sends. MessageRouter had to cast around the gap.
 */
export interface DataSnapshotMessage extends BaseMessage {
	type: MessageType.DATA_SNAPSHOT;
	/** Entity type being sent — 'hardware_alerts' added in P6a */
	entity: 'alerts' | 'work_orders' | 'approved_alerts' | 'notifications' | 'hardware_alerts';
	/**
	 * The full data payload. The shape depends on entity:
	 *  - alerts:          { regular_alerts: [...], approved_alerts: [...] }
	 *  - hardware_alerts: { items: [...], groups?: [...] }
	 *  - others:          Array<Record<string, unknown>>
	 */
	data:
		| Array<Record<string, unknown>>
		| { regular_alerts: Array<Record<string, unknown>>; approved_alerts: Array<Record<string, unknown>> }
		| { items: Array<Record<string, unknown>>; groups?: Array<Record<string, unknown>> };
	/** Total count */
	count: number;
	/** Whether this is an initial load (page refresh) vs periodic update */
	is_initial?: boolean;
	/** Whether this is a partial update (subset of data) */
	is_partial?: boolean;
	/** Number of groups (for grouped data like alerts by asset) */
	groups?: number;
	/** Backend-reported data quality. See DataQualityStatusSchema in schemas.ts. */
	data_status?: 'fresh' | 'stale' | 'site_data_missing' | 'empty_cache' | 'unavailable' | 'unknown';
	/**
	 * Top-level snapshot timestamp (ISO 8601). Backend sometimes also
	 * includes it inside metadata — DataSyncService.extractSnapshotTs()
	 * checks both locations.
	 */
	snapshot_ts?: string;
	/**
	 * Identifies which backend component failed, for the 3-layer health
	 * UI. Values: 'external_db' | 'frontend_db' | 'cache' | 'opa' | etc.
	 */
	failed_component?: string;
	/** Additional metadata */
	metadata?: Record<string, unknown>;
}

export interface ClientUpdateDataPayload {
	update_id?: string;
	update_type:
		| 'user_action'
		| 'system_event'
		| 'notification'
		| 'presence'
		| 'data_sync'
		| 'presence_heartbeat'
		| 'presence_request'
		| 'state_update';
	entity_type?: string;
	entity_id?: string | number;
	changes?: Record<string, UpdateChange>;
	// Fields for state_update
	namespace?: string;
	key?: string;
	data?: any;
}

export interface ServerUpdateDataPayload extends Omit<ClientUpdateDataPayload, 'update_id'> {
	update_id: string; // ID from server (can be same as client or new)
	source_user_id: User['id']; // Added by server
	timestamp: string; // Added by server (ISO 8601)
}

export interface ClientUpdateMessage extends BaseMessage {
	// Assuming BaseMessage exists
	type: MessageType.UPDATE;
	data: ClientUpdateDataPayload;
}

export interface ServerUpdateMessage extends BaseMessage {
	type: MessageType.UPDATE;
	data_type?: 'update';
	data: ServerUpdateDataPayload | StateUpdatePayload;
	topic?: 'updates';
}

export interface UpdateResponseMessage extends BaseMessage {
	type: MessageType.UPDATE_RESPONSE;
	status: 'broadcasted' | 'failed';
	recipients: number;
	update_id: string;
}

// Initial data complete message
export interface InitialDataCompleteMessage extends BaseMessage {
	type: MessageType.INITIAL_DATA_COMPLETE;
	loaded: string[];
}

// Request data message
export interface RequestDataMessage extends BaseMessage {
	type: MessageType.REQUEST_DATA;
	dataType: 'alerts' | 'work_orders' | 'all';
	reason?: string;
}

// WebSocket connection status message (renamed to avoid conflict with chat types)
export interface WSConnectionStatusMessage extends BaseMessage {
	type: MessageType.CONNECTION_STATUS;
	status: 'connected' | 'error';
	message: string;
	user_id?: string;
	auth_method?: 'jwt' | 'api_key';
	auto_subscribed?: string[];
	supported_message_types?: string[];
}

// Chat message interfaces
export interface SendChatMessage extends BaseMessage {
	type: MessageType.SEND_CHAT_MESSAGE;
	room_id: string;
	content: string;
	message_type?: 'text' | 'file' | 'image';
}

export interface ChatMessageResponse extends BaseMessage {
	type: MessageType.CHAT_MESSAGE_RESPONSE;
	status: 'success' | 'error';
	message: string;
	message_id?: string;
	room_id?: string;
}

export interface ChatMessage extends BaseMessage {
	type: MessageType.CHAT_MESSAGE;
	event: 'new_message' | 'message_updated' | 'message_deleted';
	room_id: string;
	message: {
		id: string;
		content: string;
		sender_id: string;
		sender_name: string;
		message_type: 'text' | 'file' | 'image';
		is_read: boolean;
	};
}

export interface JoinChatRoomMessage extends BaseMessage {
	type: MessageType.JOIN_CHAT_ROOM;
	room_id: string;
}

export interface ChatRoomJoinedMessage extends BaseMessage {
	type: MessageType.CHAT_ROOM_JOINED;
	status: 'success' | 'error';
	message: string;
	room_id?: string;
	subscribed_topic?: string;
}

export interface TypingIndicatorMessage extends BaseMessage {
	type: MessageType.TYPING_INDICATOR;
	room_id: string;
	user_id?: string;
	is_typing: boolean;
}

export interface GetChatHistoryMessage extends BaseMessage {
	type: MessageType.GET_CHAT_HISTORY;
	room_id: string;
	limit?: number;
	offset?: number;
}

export interface ChatHistoryResponse extends BaseMessage {
	type: MessageType.CHAT_HISTORY_RESPONSE;
	room_id: string;
	messages: Array<{
		id: string;
		content: string;
		sender_id: string;
		sender_name: string;
		message_type: 'text' | 'file' | 'image';
		created_at: string;
		is_read: boolean;
	}>;
	count: number;
	limit: number;
	offset: number;
}

export interface ChatInitialMessage extends BaseMessage {
	type: MessageType.CHAT_INITIAL;
	rooms: Array<{
		id: string;
		name: string;
		type: 'group' | 'direct';
		participant_count: number;
		last_message?: {
			content: string;
			sender_name: string;
			timestamp: string;
		};
	}>;
}

// Notification message interfaces
export interface MarkNotificationReadMessage extends BaseMessage {
	type: MessageType.MARK_NOTIFICATION_READ;
	notification_id: string;
}

export interface NotificationReadResponse extends BaseMessage {
	type: MessageType.NOTIFICATION_READ_RESPONSE;
	status: 'success' | 'error';
	message: string;
	notification_id?: string;
}

export interface NewNotificationMessage extends BaseMessage {
	type: MessageType.NEW_NOTIFICATION;
	notification: {
		id: string;
		title: string;
		message: string;
		type: string;
		status: 'UNREAD' | 'READ';
		scope: 'user' | 'org' | 'global';
		created_at: string;
		action_url?: string;
		is_read: boolean;
	};
}

export interface NotificationCountUpdateMessage extends BaseMessage {
	type: MessageType.NOTIFICATION_COUNT_UPDATE;
	unread_count: number;
}

export interface GetNotificationHistoryMessage extends BaseMessage {
	type: MessageType.GET_NOTIFICATION_HISTORY;
	limit?: number;
	offset?: number;
	include_read?: boolean;
}

export interface NotificationHistoryResponse extends BaseMessage {
	type: MessageType.NOTIFICATION_HISTORY_RESPONSE;
	notifications: Array<{
		id: string;
		title: string;
		message: string;
		type: string;
		status: 'UNREAD' | 'READ';
		scope: 'user' | 'org' | 'global';
		is_read: boolean;
		action_url?: string;
		created_at: string;
		read_at?: string;
	}>;
	count: number;
	limit: number;
	offset: number;
	include_read: boolean;
}

export interface NotificationsInitialMessage extends BaseMessage {
	type: MessageType.NOTIFICATIONS_INITIAL;
	unread_count: number;
	recent_notifications: Array<{
		id: string;
		title: string;
		message: string;
		type: string;
		status: 'UNREAD' | 'READ';
		created_at: string;
		action_url?: string;
	}>;
}

// Subscription status message
export interface SubscriptionStatusMessage extends BaseMessage {
	type: MessageType.SUBSCRIPTION_STATUS;
	status: 'success' | 'error';
	message: string;
	topics?: string[];
}

// Initial state message
export interface InitialStateMessage extends BaseMessage {
	type: MessageType.INITIAL_STATE;
	users: Record<
		string,
		{
			user_id: string;
			client_id: string;
			status: 'online' | 'offline';
			auth_method: 'jwt' | 'api_key';
			connected_at: string;
			last_activity: string;
		}
	>;
}

// Status response message
export interface StatusResponseMessage extends BaseMessage {
	type: MessageType.STATUS_RESPONSE;
	client_id: string;
	user_id?: string;
	auth_method?: 'jwt' | 'api_key';
	subscribed_topics: string[];
	connected: boolean;
	server_time: string;
}

// Data request response message
export interface DataRequestResponseMessage extends BaseMessage {
	type: MessageType.DATA_REQUEST_RESPONSE;
	status: 'success' | 'error';
	message: string;
	data_type: string;
	reason?: string;
}

// =============================================================================
// USER PRESENCE MESSAGE INTERFACES
// =============================================================================

// User presence data structure
export interface UserPresenceData {
	user_id: string;
	display_name: string;
	email?: string; // User email address
	status: 'online' | 'away' | 'busy' | 'offline';
	last_activity: string;
	connection_count: number;
	connections?: Array<{
		client_id: string;
		connected_at: string;
		last_activity: string;
		auth_method: 'jwt' | 'api_key';
	}>;
}

// Get online users request
export interface GetOnlineUsersMessage extends BaseMessage {
	type: MessageType.GET_ONLINE_USERS;
	include_details?: boolean;
}

// Online users response
export interface OnlineUsersResponseMessage extends BaseMessage {
	type: MessageType.ONLINE_USERS_RESPONSE;
	users: UserPresenceData[];
	total_count: number;
}

// Get user status request
export interface GetUserStatusMessage extends BaseMessage {
	type: MessageType.GET_USER_STATUS;
	user_id: string;
}

// User status response
export interface UserStatusResponseMessage extends BaseMessage {
	type: MessageType.USER_STATUS_RESPONSE;
	user_id: string;
	display_name?: string; // User display name
	email?: string; // User email address
	status: 'online' | 'away' | 'busy' | 'offline';
	last_activity: string;
	connection_count: number;
	connections?: Array<{
		client_id: string;
		connected_at: string;
		last_activity: string;
		auth_method: 'jwt' | 'api_key';
	}>;
}

// Update user presence request
export interface UpdateUserPresenceMessage extends BaseMessage {
	type: MessageType.UPDATE_USER_PRESENCE;
	status: 'online' | 'away' | 'busy' | 'offline';
}

// Presence update success response
export interface PresenceUpdateSuccessMessage extends BaseMessage {
	type: MessageType.PRESENCE_UPDATE_SUCCESS;
	user_id: string;
	status: 'online' | 'away' | 'busy' | 'offline';
}

// Get presence stats request
export interface GetPresenceStatsMessage extends BaseMessage {
	type: MessageType.GET_PRESENCE_STATS;
}

// Presence stats response
export interface PresenceStatsResponseMessage extends BaseMessage {
	type: MessageType.PRESENCE_STATS_RESPONSE;
	stats: {
		total_users: number;
		online_users: number;
		away_users: number;
		busy_users: number;
		offline_users: number;
		total_connections: number;
	};
}

// Enhanced UPDATE message for user presence updates
export interface UserPresenceUpdateData {
	update_type?: 'user_presence';
	user_id: string;
	status: 'online' | 'away' | 'busy' | 'offline';
	last_activity: string;
	connection_count: number;
	display_name: string;
	email?: string; // User email address
	event_type: 'user_connected' | 'user_disconnected' | 'status_changed';
}

// Enhanced INITIAL_STATE message with presence data
export interface EnhancedInitialStateMessage extends BaseMessage {
	type: MessageType.INITIAL_STATE;
	users: Record<
		string,
		{
			user_id: string;
			client_id: string;
			status: 'online' | 'offline';
			auth_method: 'jwt' | 'api_key';
			connected_at: string;
			last_activity: string;
			display_name?: string;
			connection_count?: number;
		}
	>;
	presence_data?: {
		online_users: UserPresenceData[];
		total_online: number;
		stats: {
			total_users: number;
			online_users: number;
			away_users: number;
			busy_users: number;
			offline_users: number;
			total_connections: number;
		};
	};
}

// Additional message interfaces for newly added types
export interface NotificationsUpdateMessage extends BaseMessage {
	type: MessageType.NOTIFICATIONS_UPDATE;
	data: Array<Record<string, unknown>>;
	count: number;
	unread_count: number;
	is_initial?: boolean;
}

export interface NotificationEventMessage extends BaseMessage {
	type: MessageType.NOTIFICATION;
	data: Record<string, unknown>;
	unread_count?: number;
}

export interface DataSentMessage {
	type: MessageType.DATA_SENT;
	data_type: string;
	data?: any;
}

export interface ConnectionEstablishedMessage {
	type: MessageType.CONNECTION_ESTABLISHED;
	client_id: string;
	timestamp?: string;
}

export interface PresenceUpdatedMessage {
	type: MessageType.PRESENCE_UPDATED;
	data: {
		user_id: string;
		status: string;
		last_seen?: string;
	};
}

export interface TaskProgressMessagePayload {
	task_id: string;
	task_type: string;
	stage: string;
	status: 'started' | 'running' | 'complete' | 'error';
	progress?: number;
	message?: string;
	metadata?: Record<string, unknown>;
}

export interface TaskProgressMessage extends BaseMessage, TaskProgressMessagePayload {
	type: MessageType.TASK_PROGRESS;
}

// Union type for all messages
export type WebSocketMessage =
	| ConnectMessage
	| ConnectionAckMessage
	| WSConnectionStatusMessage
	| SubscribeMessage
	| SubscriptionAckMessage
	| SubscriptionStatusMessage
	| DataUpdateMessage
	| DataInsertMessage
	| DataDeleteMessage
	| BulkUpdateMessage
	| ErrorMessage
	| HeartbeatMessage
	| WebSocketPingMessage
	| WebSocketPongMessage
	| StatusMessage
	| StatusResponseMessage
	| FilteredAlertsUpdateMessage
	| UpdateMessage
	| ClientUpdateMessage
	| ServerUpdateMessage
	| UpdateResponseMessage
	| StateUpdateMessage
	| StateUpdateResponseMessage
	| GetStateMessage
	| StateDataMessage
	| InitialDataCompleteMessage
	| InitialStateMessage
	| RequestDataMessage
	| DataRequestResponseMessage
	| SendChatMessage
	| ChatMessageResponse
	| ChatMessage
	| JoinChatRoomMessage
	| ChatRoomJoinedMessage
	| TypingIndicatorMessage
	| GetChatHistoryMessage
	| ChatHistoryResponse
	| ChatInitialMessage
	| MarkNotificationReadMessage
	| NotificationReadResponse
	| NewNotificationMessage
	| NotificationCountUpdateMessage
	| GetNotificationHistoryMessage
	| NotificationHistoryResponse
	| NotificationsInitialMessage
	| GetOnlineUsersMessage
	| OnlineUsersResponseMessage
	| GetUserStatusMessage
	| UserStatusResponseMessage
	| UpdateUserPresenceMessage
	| PresenceUpdateSuccessMessage
	| GetPresenceStatsMessage
	| PresenceStatsResponseMessage
	| EnhancedInitialStateMessage
	| NotificationsUpdateMessage
	| NotificationEventMessage
	| DataSentMessage
	| ConnectionEstablishedMessage
	| PresenceUpdatedMessage
	| TaskProgressMessage
	| DataDeltaMessage
	| DataDeltaBatchMessage
	| DataSnapshotMessage;
